import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { approverApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import ApprovalTimeline from '../../components/employee/ApprovalTimeline.jsx';
import { ArrowLeft } from 'lucide-react';
import { formatDays } from '@/lib/utils';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'default' };

function formatDate(d) {
  if (!d) return '\u2014';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

export default function ReviewRequestPage() {
  const { userId, requestId } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [canAct, setCanAct] = useState(false);
  const [reason, setReason] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        // Dedicated endpoint — returns the request regardless of step/status
        // so we can render a sensible view even when the email link is
        // clicked after someone else in the chain already actioned it.
        const res = await approverApi.getRequest(userId, requestId);
        setRequest(res.request || null);
        setCanAct(!!res.canAct);
        setReason(res.reason || '');
      } catch (err) {
        setLoadError(err.message || 'Failed to load request');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId, requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAction() {
    if (action === 'reject' && note.trim().length < 3) {
      toast.error('Rejection reason is required (minimum 3 characters)');
      return;
    }

    setSubmitting(true);
    try {
      if (action === 'approve') {
        await approverApi.approve(userId, requestId, { note: note.trim() || undefined });
        toast.success('Request approved');
      } else {
        await approverApi.reject(userId, requestId, { reason: note.trim() });
        toast.success('Request rejected');
      }
      navigate('/approvals');
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <span className="ml-3 text-sm">Loading...</span>
      </div>
    );
  }

  if (loadError || !request) {
    // Surface the real backend error when present — helps distinguish
    // "wrong account / not an approver" (403) from "request was deleted"
    // (404) instead of the catch-all we used to show.
    const msg = loadError || 'Request not found.';
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        {msg}
        <br /><br />
        <Link to="/approvals" className="text-primary">Back to queue</Link>
      </div>
    );
  }

  // Human-readable banner for non-actionable states. Keeps the email link
  // useful even after the request has moved on.
  const statusBanner = (() => {
    if (canAct) return null;
    if (reason === 'already_approved') {
      return { tone: 'success', text: 'This request has already been approved. Nothing for you to do.' };
    }
    if (reason === 'already_rejected') {
      return { tone: 'danger', text: 'This request was rejected. Nothing for you to do.' };
    }
    if (reason === 'cancelled') {
      return { tone: 'default', text: 'This request was cancelled by the requester.' };
    }
    if (reason === 'waiting_earlier_step') {
      const prev = (request.chainSnapshot || [])[request.currentStep || 0];
      const who = prev?.label || prev?.email || 'an earlier approver';
      return { tone: 'warning', text: `Waiting for ${who} to act first. You'll be able to decide once it reaches your step.` };
    }
    if (reason === 'step_already_decided') {
      return { tone: 'default', text: 'Your step in this request has already been decided.' };
    }
    if (reason === 'not_on_chain') {
      return { tone: 'danger', text: 'You are not an approver on this request.' };
    }
    return { tone: 'default', text: 'This request is not currently actionable by you.' };
  })();

  return (
    <div className="max-w-2xl">
      <Link to="/approvals" className="mb-4 inline-flex items-center gap-1 text-sm text-primary no-underline hover:underline">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Approval Queue
      </Link>

      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Review Request</h1>
          <Badge variant={STATUS_VARIANT[request.status] || 'default'}>{request.status}</Badge>
        </div>
        <p className="text-muted-foreground">
          From {request.requesterName} ({request.requesterDepartment || '\u2014'})
        </p>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4">
            <DetailItem label="Leave Type" value={request.typeName || request.typeId} />
            <DetailItem label="Days" value={`${formatDays(request.days)} working day${request.days !== 1 ? 's' : ''}`} />
            <DetailItem label="Start Date" value={formatDate(request.startDate)} />
            <DetailItem label="End Date" value={formatDate(request.endDate)} />
            <DetailItem label="Submitted" value={request.submittedAt ? new Date(request.submittedAt).toLocaleString() : '\u2014'} />
          </div>
          {request.reason && (
            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-1 text-xs text-muted-foreground">Reason</div>
              <div className="text-sm">{request.reason}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mb-6">
        <h2 className="mb-4 text-base font-semibold">Approval Progress</h2>
        <ApprovalTimeline
          chainSnapshot={request.chainSnapshot}
          currentStep={request.currentStep}
          status={request.status}
        />
      </div>

      {statusBanner && (
        <div
          className={
            'mb-4 rounded-md border px-4 py-3 text-sm ' +
            (statusBanner.tone === 'success'
              ? 'border-success/30 bg-success/10 text-success'
              : statusBanner.tone === 'danger'
              ? 'border-destructive/30 bg-destructive/10 text-destructive'
              : statusBanner.tone === 'warning'
              ? 'border-warning/30 bg-warning/10 text-warning'
              : 'border-border bg-muted text-muted-foreground')
          }
        >
          {statusBanner.text}
        </div>
      )}

      {canAct && (
        <div className="flex gap-3">
          <Button variant="danger" onClick={() => setAction('reject')}>Reject</Button>
          <Button onClick={() => setAction('approve')}>Approve</Button>
        </div>
      )}

      {action && (
        <Modal
          isOpen={true}
          onClose={() => { setAction(null); setNote(''); }}
          title={action === 'approve' ? 'Approve Request' : 'Reject Request'}
          width="480px"
          footer={
            <>
              <Button variant="ghost" onClick={() => { setAction(null); setNote(''); }}>Cancel</Button>
              <Button variant={action === 'approve' ? 'primary' : 'danger'} onClick={handleAction} loading={submitting}>
                {action === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {action === 'approve' ? 'Note (optional)' : 'Reason for rejection (required)'}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={action === 'approve' ? 'Add an optional note...' : 'Explain why...'}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ resize: 'vertical' }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-medium">{value}</div>
    </div>
  );
}
